/**
 * ----------------------------------------------------------------------------
 * WAITLIST SIGNUP
 * ----------------------------------------------------------------------------
 * Custom addition to the Impact theme. Not part of Maestrooo's base code.
 *
 * The waitlist form submits NATIVELY, exactly like the same form on the product
 * page, which works. The shop is behind bot protection (a Cloudflare managed
 * challenge plus Shopify's hCaptcha on customer forms) built around a full-page
 * submission: the challenge page runs, gets its clearance, and lets the POST
 * through. Fetch and hidden-iframe tricks all fought that protection and failed
 * (three dead ends on 23/07/2026), so this component adds NOTHING to the
 * submission itself. It only smooths the two things a reload costs on a
 * collection page:
 *
 *   - on submit, it points return_to at the full current URL (filters kept)
 *     with waitlist_posted=<product id>, and remembers the scroll position;
 *   - on the shopper's return, the one component whose product matches reopens
 *     its drawer, restores the scroll, and shows its confirmation. No other
 *     drawer reacts, so the "confirmation in every drawer" bug cannot happen.
 *
 * Server-side confirmation is deliberately avoided: form.posted_successfully?
 * is true for every customer form on the page after any post, this is scoped
 * to a single product instead.
 *
 * Without JavaScript the email is still recorded; only the on-screen
 * confirmation needs this script.
 */

const SCROLL_KEY = 'waitlist:scroll';
const PRODUCT_KEY = 'waitlist:product';

class WaitlistSignup extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');

    if (!this.form) {
      return;
    }

    this.productId = this.dataset.productId;
    this.form.addEventListener('submit', () => this.onSubmit());
    this.revealIfPosted();
  }

  /**
   * Let the native submission fly, but first make the round-trip come back to
   * this exact spot with a marker naming this product.
   *
   * @returns {void}
   */
  onSubmit() {
    const returnTo = this.form.elements['return_to'];

    if (returnTo) {
      const url = new URL(window.location.href);
      url.searchParams.set('waitlist_posted', this.productId);
      returnTo.value = url.pathname + url.search;
    }

    try {
      sessionStorage.setItem(PRODUCT_KEY, this.productId);
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    } catch {
      /* Private mode with no storage quota: the signup still works, only the
         scroll restore and the no-marker fallback are skipped. */
    }

    this.setBusy(true);
  }

  /**
   * On landing back after a successful post, reopen this product's drawer with
   * its confirmation. Runs on every instance; only the matching one acts.
   *
   * @returns {void}
   */
  revealIfPosted() {
    const params = new URLSearchParams(window.location.search);

    if (params.get('customer_posted') !== 'true' && !params.has('waitlist_posted')) {
      return;
    }

    /* waitlist_posted rides return_to; the sessionStorage copy covers the case
       where Shopify redirects to its default page and drops the query. */
    let target = params.get('waitlist_posted');
    if (!target) {
      try {
        target = sessionStorage.getItem(PRODUCT_KEY);
      } catch {
        target = null;
      }
    }

    if (target !== this.productId) {
      return;
    }

    this.showSuccess();
    this.restoreScroll();
    this.closest('x-drawer')?.show();

    try {
      sessionStorage.removeItem(PRODUCT_KEY);
    } catch {
      /* Nothing to clear. */
    }

    /* Clean the markers so a refresh does not replay the confirmation. */
    params.delete('customer_posted');
    params.delete('waitlist_posted');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
  }

  /**
   * Restore the scroll position captured at submit time, once.
   *
   * @returns {void}
   */
  restoreScroll() {
    let stored = null;

    try {
      stored = sessionStorage.getItem(SCROLL_KEY);
      sessionStorage.removeItem(SCROLL_KEY);
    } catch {
      return;
    }

    if (stored !== null) {
      window.scrollTo(0, parseInt(stored, 10) || 0);
    }
  }

  /**
   * Swap the fields for the confirmation message.
   *
   * @returns {void}
   */
  showSuccess() {
    this.querySelector('[data-waitlist-fields]')?.setAttribute('hidden', '');
    this.querySelector('[data-waitlist-success]')?.removeAttribute('hidden');
  }

  /**
   * Mark the submit button busy while the page navigates through the challenge.
   *
   * @param {boolean} busy - Whether a submission is in flight
   * @returns {void}
   */
  setBusy(busy) {
    const submitButton = this.form.querySelector('[type="submit"]');

    if (busy) {
      submitButton?.setAttribute('aria-busy', 'true');
    } else {
      submitButton?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('waitlist-signup')) {
  customElements.define('waitlist-signup', WaitlistSignup);
}
